import React, { useState } from 'react';
import { reportsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { ChartBar, Download, MagnifyingGlass, FileXls, FilePdf, FileCsv } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const getDefaultDates = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const to = now.toISOString().split('T')[0];
  return { from, to };
};

const ReportsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('claims');
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [loading, setLoading] = useState(false);

  const [claimsReport, setClaimsReport] = useState([]);
  const [payerReport, setPayerReport] = useState([]);
  const [denialReport, setDenialReport] = useState([]);
  const [arReport, setArReport] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [summaryDate, setSummaryDate] = useState(new Date().toISOString().split('T')[0]);
  const [branchReport, setBranchReport] = useState([]);
  const [userReport, setUserReport] = useState([]);

  const runReport = async (type) => {
    setLoading(true);
    try {
      switch (type) {
        case 'claims': {
          const res = await reportsApi.claims({ from_date: fromDate, to_date: toDate });
          setClaimsReport(res.data);
          toast.success(`Loaded ${res.data.length} claims`);
          break;
        }
        case 'payer': {
          const res = await reportsApi.payerSettlement({ from_date: fromDate, to_date: toDate });
          setPayerReport(res.data);
          toast.success(`Loaded ${res.data.length} payer records`);
          break;
        }
        case 'denial': {
          const res = await reportsApi.denialAnalysis({ from_date: fromDate, to_date: toDate });
          setDenialReport(res.data);
          toast.success(`Loaded ${res.data.length} denial records`);
          break;
        }
        case 'ar': {
          const res = await reportsApi.arAging();
          setArReport(res.data);
          toast.success(`Loaded ${res.data.length} AR records`);
          break;
        }
        case 'daily': {
          const res = await reportsApi.dailySummary(summaryDate);
          setDailySummary(res.data);
          toast.success('Daily summary loaded');
          break;
        }
        case 'branch': {
          const res = await reportsApi.branchWise({ from_date: fromDate, to_date: toDate });
          setBranchReport(res.data);
          toast.success(`Loaded ${res.data.length} branch records`);
          break;
        }
        case 'user': {
          const res = await reportsApi.userProductivity({ from_date: fromDate, to_date: toDate });
          setUserReport(res.data);
          toast.success(`Loaded ${res.data.length} user records`);
          break;
        }
        default: break;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async (type) => {
    try {
      let res;
      const params = { from_date: fromDate, to_date: toDate };
      switch (type) {
        case 'claims': res = await reportsApi.claimsCsv(params); break;
        case 'payer': res = await reportsApi.payerSettlementCsv(params); break;
        case 'denial': res = await reportsApi.denialAnalysisCsv(params); break;
        case 'ar': res = await reportsApi.arAgingCsv(); break;
        default: return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const getExportData = (type) => {
    switch (type) {
      case 'claims': return { data: claimsReport, columns: ['claim_number', 'insurance_claim_no', 'patient_name', 'payer_name', 'department', 'status', 'billed_amount', 'approved_amount', 'paid_amount', 'outstanding'], headers: ['Case No', 'Claim No', 'Patient', 'Payer', 'Dept', 'Status', 'Billed', 'Approved', 'Paid', 'Outstanding'] };
      case 'ar': return { data: arReport, columns: ['claim_number', 'insurance_claim_no', 'patient_name', 'payer_name', 'billed_amount', 'paid_amount', 'outstanding', 'days_old', 'aging_bucket', 'status'], headers: ['Case No', 'Claim No', 'Patient', 'Payer', 'Billed', 'Paid', 'Outstanding', 'Days', 'Bucket', 'Status'] };
      case 'payer': return { data: payerReport, columns: ['payer_name', 'total_claims', 'total_billed', 'total_approved', 'total_paid', 'total_outstanding', 'avg_settlement_days'], headers: ['Payer', 'Cases', 'Billed', 'Approved', 'Paid', 'Outstanding', 'Avg Days'] };
      case 'denial': return { data: denialReport, columns: ['category', 'count', 'total_amount', 'recovered_amount', 'recovery_rate'], headers: ['Category', 'Count', 'Total Amount', 'Recovered', 'Recovery Rate'] };
      case 'branch': return { data: branchReport, columns: ['branch_name', 'total_claims', 'total_billed', 'total_approved', 'total_paid'], headers: ['Branch', 'Cases', 'Billed', 'Approved', 'Paid'] };
      case 'user': return { data: userReport, columns: ['user_name', 'claims_handled', 'total_billed', 'total_collected'], headers: ['User', 'Cases Handled', 'Billed', 'Collected'] };
      default: return { data: [], columns: [], headers: [] };
    }
  };

  const downloadExcel = (type) => {
    const { data, columns, headers } = getExportData(type);
    if (!data?.length) { toast.error('No data to export. Run the report first.'); return; }
    const rows = data.map(row => columns.map(col => row[col] ?? ''));
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // Auto column width
    ws['!cols'] = headers.map((h, i) => ({ wch: Math.max(h.length, ...rows.map(r => String(r[i] || '').length).slice(0, 50)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, type.charAt(0).toUpperCase() + type.slice(1) + ' Report');
    XLSX.writeFile(wb, `${type}_report_${fromDate}_${toDate}.xlsx`);
    toast.success('Excel downloaded');
  };

  const downloadPdf = (type) => {
    const { data, columns, headers } = getExportData(type);
    if (!data?.length) { toast.error('No data to export. Run the report first.'); return; }
    const doc = new jsPDF({ orientation: headers.length > 7 ? 'landscape' : 'portrait', unit: 'mm' });
    const title = type.charAt(0).toUpperCase() + type.slice(1) + ' Report';
    const pageW = doc.internal.pageSize.getWidth();

    // Hospital Header
    const hospitalName = user?.tenant_name || user?.hospital_name || 'Hospital';
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(hospitalName, pageW / 2, 14, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Revenue Cycle Management System', pageW / 2, 19, { align: 'center' });

    // Divider line
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(10, 22, pageW - 10, 22);

    // Report title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30);
    doc.text(title, 14, 29);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Period: ${fromDate || 'All'} to ${toDate || 'All'}  |  Generated: ${new Date().toLocaleString('en-IN')}`, 14, 34);

    const rows = data.map(row => columns.map(col => {
      const v = row[col];
      if (v === null || v === undefined) return '-';
      if (typeof v === 'number') return v.toLocaleString('en-IN');
      return String(v);
    }));
    autoTable(doc, {
      head: [headers], body: rows, startY: 38,
      styles: { fontSize: 8, cellPadding: 2.5, font: 'helvetica' },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: columns.reduce((acc, col, i) => {
        if (['billed_amount', 'approved_amount', 'paid_amount', 'outstanding', 'total_billed', 'total_approved', 'total_paid', 'total_outstanding', 'amount', 'deduction_amount', 'total_amount', 'recovered_amount'].includes(col)) {
          acc[i] = { halign: 'right', fontStyle: 'bold' };
        }
        return acc;
      }, {}),
      margin: { left: 10, right: 10 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 15, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
      doc.text('Care\'O - Hospital Claim Management System', 14, doc.internal.pageSize.getHeight() - 8);
    }

    doc.save(`${type}_report_${fromDate || 'all'}_${toDate || 'all'}.pdf`);
    toast.success('PDF downloaded');
  };

  // HIPAA: Export restriction - only allowed roles can download data
  const EXPORT_ALLOWED_ROLES = ['super_admin', 'hospital_admin', 'finance_manager'];
  const canExport = user?.roles ? user.roles.some(r => EXPORT_ALLOWED_ROLES.includes(r)) : EXPORT_ALLOWED_ROLES.includes(user?.role);

  const ExportButtons = ({ type }) => (
    <div className="flex gap-1.5">
      <Button variant="outline" size="sm" className="gap-1.5 text-xs border-emerald-200 text-emerald-600 hover:bg-emerald-50 h-8"
        onClick={() => downloadExcel(type)} disabled={!canExport} title={!canExport ? 'Export restricted by HIPAA policy' : ''} data-testid={`export-excel-${type}`}>
        <FileXls className="h-3.5 w-3.5" /> Excel
      </Button>
      <Button variant="outline" size="sm" className="gap-1.5 text-xs border-red-200 text-red-600 hover:bg-red-50 h-8"
        onClick={() => downloadPdf(type)} disabled={!canExport} title={!canExport ? 'Export restricted by HIPAA policy' : ''} data-testid={`export-pdf-${type}`}>
        <FilePdf className="h-3.5 w-3.5" /> PDF
      </Button>
      <Button variant="outline" size="sm" className="gap-1.5 text-xs border-sky-200 text-sky-600 hover:bg-sky-50 h-8"
        onClick={() => downloadCsv(type)} disabled={!canExport} title={!canExport ? 'Export restricted by HIPAA policy' : ''} data-testid={`export-csv-${type}`}>
        <FileCsv className="h-3.5 w-3.5" /> CSV
      </Button>
      {!canExport && <span className="text-[9px] text-amber-600 self-center ml-1">Export restricted</span>}
    </div>
  );

  const StatusBadge = ({ status }) => {
    const colors = {
      settled: 'bg-emerald-100 text-emerald-700',
      submitted: 'bg-sky-100 text-sky-700',
      denied: 'bg-red-100 text-red-700',
      partially_settled: 'bg-amber-100 text-amber-700',
    };
    const cls = colors[status] || 'bg-slate-100 text-slate-600';
    return <Badge className={`${cls} border-0 text-xs`}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Toaster position="top-right" richColors />
      
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 tracking-tight font-['Outfit']" data-testid="reports-title">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Generate and download hospital-specific reports</p>
      </div>

      {/* Date Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs text-slate-500">From Date</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-44 border-slate-300" data-testid="report-from-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To Date</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-44 border-slate-300" data-testid="report-to-date" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 border border-slate-200 p-1" data-testid="report-tabs">
          <TabsTrigger value="claims" className="data-[state=active]:bg-white data-[state=active]:text-sky-700 data-[state=active]:shadow-sm text-sm">Claims Report</TabsTrigger>
          <TabsTrigger value="payer" className="data-[state=active]:bg-white data-[state=active]:text-violet-700 data-[state=active]:shadow-sm text-sm">Payer Settlement</TabsTrigger>
          <TabsTrigger value="denial" className="data-[state=active]:bg-white data-[state=active]:text-rose-700 data-[state=active]:shadow-sm text-sm">Denial Analysis</TabsTrigger>
          <TabsTrigger value="ar" className="data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm text-sm">AR Aging</TabsTrigger>
          <TabsTrigger value="daily" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm text-sm">Daily Summary</TabsTrigger>
          <TabsTrigger value="branch" className="data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm text-sm">Branch-wise</TabsTrigger>
          <TabsTrigger value="user" className="data-[state=active]:bg-white data-[state=active]:text-pink-700 data-[state=active]:shadow-sm text-sm">User Productivity</TabsTrigger>
        </TabsList>

        {/* Claims Report */}
        <TabsContent value="claims" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => runReport('claims')} disabled={loading} className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-sm gap-2" data-testid="run-claims-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {claimsReport.length > 0 && (
              <ExportButtons type="claims" />
            )}
          </div>
          {claimsReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="claims-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Case No</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Claim No</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Patient</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Dept</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Billed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Approved</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Paid</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Outstanding</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {claimsReport.map((c, idx) => (
                        <TableRow key={idx} className="hover:bg-sky-50/50 transition-colors">
                          <TableCell className="text-sm font-semibold text-sky-700">{c.claim_number}</TableCell>
                          <TableCell className="text-xs font-mono text-orange-600">{c.insurance_claim_no || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{c.patient_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{c.payer_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{c.department}</TableCell>
                          <TableCell><StatusBadge status={c.status} /></TableCell>
                          <TableCell className="text-sm text-right">{formatCurrency(c.billed_amount)}</TableCell>
                          <TableCell className="text-sm text-right text-violet-600">{formatCurrency(c.approved_amount)}</TableCell>
                          <TableCell className="text-sm text-right font-medium text-emerald-600">{formatCurrency(c.paid_amount)}</TableCell>
                          <TableCell className="text-sm text-right font-medium text-rose-600">{formatCurrency(c.outstanding)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-3 text-xs text-slate-500">Total: {claimsReport.length} records</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payer Settlement Report */}
        <TabsContent value="payer" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => runReport('payer')} disabled={loading} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-sm gap-2" data-testid="run-payer-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {payerReport.length > 0 && (
              <ExportButtons type="payer" />
            )}
          </div>
          {payerReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="payer-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Claims</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Billed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Approved</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Paid</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Deductions</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Outstanding</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Denied</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Realization %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payerReport.map((r, idx) => (
                        <TableRow key={idx} className="hover:bg-violet-50/50 transition-colors">
                          <TableCell className="text-sm font-semibold text-violet-700">{r.payer}</TableCell>
                          <TableCell className="text-sm text-right">{r.total_claims}</TableCell>
                          <TableCell className="text-sm text-right">{formatCurrency(r.total_billed)}</TableCell>
                          <TableCell className="text-sm text-right text-violet-600">{formatCurrency(r.total_approved)}</TableCell>
                          <TableCell className="text-sm text-right font-medium text-emerald-600">{formatCurrency(r.total_paid)}</TableCell>
                          <TableCell className="text-sm text-right text-amber-600">{formatCurrency(r.total_deductions)}</TableCell>
                          <TableCell className="text-sm text-right text-rose-600">{formatCurrency(r.outstanding)}</TableCell>
                          <TableCell className="text-sm text-right">{r.denied_claims}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-sky-600">{r.realization_rate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Denial Analysis Report */}
        <TabsContent value="denial" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => runReport('denial')} disabled={loading} className="bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white shadow-sm gap-2" data-testid="run-denial-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {denialReport.length > 0 && (
              <ExportButtons type="denial" />
            )}
          </div>
          {denialReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="denial-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Count</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Deduction</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Recovery</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Net Loss</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {denialReport.map((d, idx) => (
                        <TableRow key={idx} className="hover:bg-rose-50/50 transition-colors">
                          <TableCell className="text-sm font-semibold text-rose-700">{d.category?.replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-sm text-slate-600">{d.payer}</TableCell>
                          <TableCell className="text-sm text-right">{d.count}</TableCell>
                          <TableCell className="text-sm text-right text-red-600">{formatCurrency(d.total_deduction)}</TableCell>
                          <TableCell className="text-sm text-right text-emerald-600">{formatCurrency(d.total_recovery)}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-amber-600">{formatCurrency(d.net_loss)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AR Aging Report */}
        <TabsContent value="ar" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => runReport('ar')} disabled={loading} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-sm gap-2" data-testid="run-ar-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {arReport.length > 0 && (
              <ExportButtons type="ar" />
            )}
          </div>
          {arReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="ar-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Case No</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Claim No</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Patient</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Billed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Paid</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Outstanding</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Days</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Bucket</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {arReport.map((r, idx) => {
                        const bucketColors = {
                          '0-30': 'bg-emerald-100 text-emerald-700',
                          '31-60': 'bg-amber-100 text-amber-700',
                          '61-90': 'bg-orange-100 text-orange-700',
                          '90+': 'bg-red-100 text-red-700',
                        };
                        return (
                          <TableRow key={idx} className="hover:bg-amber-50/50 transition-colors">
                            <TableCell className="text-sm font-semibold text-amber-700">{r.claim_number}</TableCell>
                            <TableCell className="text-xs font-mono text-orange-600">{r.insurance_claim_no || '-'}</TableCell>
                            <TableCell className="text-sm text-slate-600">{r.patient_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{r.payer_name}</TableCell>
                            <TableCell className="text-sm text-right">{formatCurrency(r.billed_amount)}</TableCell>
                            <TableCell className="text-sm text-right text-emerald-600">{formatCurrency(r.paid_amount)}</TableCell>
                            <TableCell className="text-sm text-right font-bold text-rose-600">{formatCurrency(r.outstanding)}</TableCell>
                            <TableCell className="text-sm text-right">{r.days_old}</TableCell>
                            <TableCell>
                              <Badge className={`${bucketColors[r.aging_bucket] || ''} border-0 text-xs`}>{r.aging_bucket} days</Badge>
                            </TableCell>
                            <TableCell><StatusBadge status={r.status} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-3 text-xs text-slate-500">Total: {arReport.length} open claims</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Daily Summary */}
        <TabsContent value="daily" className="space-y-4">
          <div className="flex items-end gap-4">
            <div>
              <Label className="text-xs text-slate-500">Date</Label>
              <Input type="date" value={summaryDate} onChange={e => setSummaryDate(e.target.value)} className="w-44 border-slate-300" data-testid="daily-summary-date" />
            </div>
            <Button onClick={() => runReport('daily')} disabled={loading} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-sm gap-2" data-testid="run-daily-summary">
              <MagnifyingGlass className="h-4 w-4" /> Get Summary
            </Button>
          </div>
          {dailySummary && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="daily-summary-cards">
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <CardContent className="pt-5 pb-4 text-center relative">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">New Claims</p>
                  <p className="text-3xl font-bold text-slate-800 mt-2 font-['Outfit']">{dailySummary.new_claims}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-blue-500"></div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <CardContent className="pt-5 pb-4 text-center relative">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Submissions</p>
                  <p className="text-3xl font-bold text-violet-600 mt-2 font-['Outfit']">{dailySummary.submissions}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-400 to-purple-500"></div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <CardContent className="pt-5 pb-4 text-center relative">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Payments</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-2 font-['Outfit']">{dailySummary.payments_count}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <CardContent className="pt-5 pb-4 text-center relative">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Collected</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-2 font-['Outfit']">{formatCurrency(dailySummary.payments_amount)}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-emerald-500"></div>
                </CardContent>
              </Card>
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <CardContent className="pt-5 pb-4 text-center relative">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Denials</p>
                  <p className="text-3xl font-bold text-rose-600 mt-2 font-['Outfit']">{dailySummary.denials}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-400 to-red-500"></div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Branch-wise Report */}
        <TabsContent value="branch" className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => runReport('branch')} disabled={loading} className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white shadow-sm gap-2" data-testid="run-branch-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {branchReport.length > 0 && <ExportButtons type="branch" />}
          </div>
          {branchReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="branch-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Branch</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Claims</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Billed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Approved</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Paid</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Outstanding</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Submitted</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Denied</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Settled</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Realization %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {branchReport.map((r, idx) => (
                        <TableRow key={idx} className="hover:bg-teal-50/50 transition-colors">
                          <TableCell className="text-sm font-semibold text-teal-700">{r.branch_name}</TableCell>
                          <TableCell className="text-sm text-right">{r.total_claims}</TableCell>
                          <TableCell className="text-sm text-right">{formatCurrency(r.total_billed)}</TableCell>
                          <TableCell className="text-sm text-right text-violet-600">{formatCurrency(r.total_approved)}</TableCell>
                          <TableCell className="text-sm text-right text-emerald-600">{formatCurrency(r.total_paid)}</TableCell>
                          <TableCell className="text-sm text-right text-rose-600">{formatCurrency(r.outstanding)}</TableCell>
                          <TableCell className="text-sm text-right">{r.submitted}</TableCell>
                          <TableCell className="text-sm text-right">{r.denied}</TableCell>
                          <TableCell className="text-sm text-right">{r.settled}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-sky-600">{r.realization_rate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* User Productivity Report */}
        <TabsContent value="user" className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => runReport('user')} disabled={loading} className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white shadow-sm gap-2" data-testid="run-user-report">
              <MagnifyingGlass className="h-4 w-4" /> Generate
            </Button>
            {userReport.length > 0 && <ExportButtons type="user" />}
          </div>
          {userReport.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="user-report-table">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Claims Created</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total Billed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Avg Claim Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userReport.map((r, idx) => (
                        <TableRow key={idx} className="hover:bg-pink-50/50 transition-colors">
                          <TableCell>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{r.user_name}</p>
                              <p className="text-[10px] text-slate-400">{r.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell><Badge className="bg-violet-100 text-violet-700 border-0 text-xs capitalize">{r.role?.replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-sm text-right font-bold text-pink-700">{r.claims_created}</TableCell>
                          <TableCell className="text-sm text-right text-emerald-600">{formatCurrency(r.total_billed)}</TableCell>
                          <TableCell className="text-sm text-right text-sky-600">{formatCurrency(r.avg_claim_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
